import Quill from 'quill';
import QuillCursors from 'quill-cursors';
import Delta from 'quill-delta';
import 'quill/dist/quill.snow.css';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import colors from '../constants/AvatarsColors';
import Client from './Client';
import ReactQuill from './Editors/QuillEditor';
import ErrorMessage from './ErrorMessage';
import TextSelectionEditor from './TextSelectionEditor';

Quill.register('modules/cursors', QuillCursors);

const CollaborativeEditor = ({
  quillEditorRef,
  generatedContentId,
  aiModel,
  provider,
  controllerRef,
  setShowAbortButton,
}) => {
  const { user } = useSelector((state) => state.auth);
  const email = user.email;
  const [clients, setClients] = useState([]);
  const [textSelectionEditorOpen, setTextSelectionEditorOpen] = useState(false);
  const [textSelectionEditor, setTextSelectionEditor] = useState();
  const [selectedText, setSelectedText] = useState();
  const [selectedTextRange, setSelectedTextRange] = useState();
  const [error, setError] = useState();

  const editorContainerRef = useRef(null);

  const handleTextSelectionEditorOpen = (value) => {
    setTextSelectionEditorOpen(value);
  };

  useEffect(() => {
    let currentSocket, currentEditor;

    const handleTextChange = (delta, oldDelta, source) => {
      if (source !== 'user') {
        return;
      }
      handleTextSelectionEditorOpen(false);

      currentSocket.emit('text-change', { generatedContentId, email, delta: delta });

      const currentContents = quillEditorRef.current.getEditor().getContents();
      const serializedContent = JSON.stringify(currentContents);
      currentSocket.emit('save-document', { generatedContentId, content: serializedContent });

      const range = quillEditorRef.current.getEditor().getSelection();
      if (range && range.length === 0) {
        currentSocket.emit('cursor-move', { generatedContentId, email, cursorPos: range });
      }
    };

    const handleSelectionChange = (range) => {
      currentSocket.emit('cursor-selection', { generatedContentId, email, cursorPos: range });

      if (!range?.length) {
        handleTextSelectionEditorOpen(false);
        return;
      }

      const quill = quillEditorRef.current.getEditor();
      const selectedQuillText = quill.getText(range.index, range.length);

      if (selectedQuillText.trim() === '') {
        handleTextSelectionEditorOpen(false);
        return;
      }

      handleTextSelectionEditorOpen(true);
      setSelectedTextRange(range);
      setSelectedText(selectedQuillText);

      const startIndex = range?.index;
      let currentIndex = startIndex;
      let cumulativeHeight = 0;
      let topPosition = 0;
      let isSelectionGroup = true;
      const singleLineHeight = 38;
      const textLeftOffsetConstant = 70;
      const preventOverlapOnFullTextSelection = 30;

      // Loop through the selection range
      // to calculate the cumulative height of the selection
      while (currentIndex < startIndex + range.length) {
        const [line] = quill.getLine(currentIndex);
        if (line) {
          const lineNode = line.domNode;
          const rect = lineNode.getBoundingClientRect();
          cumulativeHeight += rect.height;

          if (startIndex === 0 && currentIndex === 0 && rect.height < singleLineHeight) {
            topPosition = rect.top + rect.height;
          } else if (isSelectionGroup) {
            // with multiple selected lines/paragraphs the top might be pushed further down
            // this brings it back up
            topPosition = rect.top + rect.height / 1.5;
            isSelectionGroup = false;
          }

          currentIndex += line.length();
        } else {
          break;
        }
      }

      const containerRect = editorContainerRef.current.getBoundingClientRect();
      const containerHeight = editorContainerRef.current.clientHeight;
      const cumulativeTop = topPosition - containerRect.top + cumulativeHeight + 20;

      setTextSelectionEditor({
        top: cumulativeTop > containerHeight ? containerHeight + preventOverlapOnFullTextSelection : cumulativeTop,
        left: textLeftOffsetConstant,
      });
    };

    const handleSocketFunctionality = (socket, editor) => {
      socket.on('connect', () => {
        socket.emit('join-room', { generatedContentId, email });
      });

      socket.on('user-joined', (email) => {
        setClients((prev) => [...prev, email]);

        const cursors = editor.getModule('cursors');
        const allCursors = cursors.cursors();

        if (!allCursors.some((cursor) => cursor.id === email)) {
          const userColor = colors[(email.charCodeAt(0) - 65) % 19];
          cursors.createCursor(email, email, userColor);
        }

        toast.success(`${email} has joined the room.`);
      });

      socket.on('connected-users', (users) => setClients(users));

      socket.on('initialize-document', (documentState) => {
        if (!documentState) {
          return;
        }

        const delta = new Delta(JSON.parse(documentState));
        editor.setContents(delta, 'silent');
      });

      socket.on('text-change', (data) => {
        if (data.email !== email) {
          const delta = new Delta(data.delta);
          editor.updateContents(delta, 'silent');
        }
      });

      socket.on('user-left', (email) => {
        setClients((prev) => prev.filter((user) => user !== email));
        const cursors = editor.getModule('cursors');
        cursors.removeCursor(email, email);
        toast.success(`${email} has left the room.`);
      });

      socket.on('remote-cursor-selection', ({ email, cursorPos }) => {
        const cursors = editor.getModule('cursors');
        const allCursors = cursors.cursors();

        if (!allCursors.some((cursor) => cursor.id === email)) {
          const currentUserColor = colors[(email.charCodeAt(0) - 65) % 19];
          cursors.createCursor(email, email, currentUserColor);
        }

        cursors.moveCursor(email, cursorPos);
        cursors.toggleFlag(email, true);
      });

      socket.on('remote-cursor-move', ({ email, cursorPos }) => {
        const cursors = editor.getModule('cursors');
        cursors.moveCursor(email, cursorPos);
        cursors.toggleFlag(email, true);
      });

      editor.on('text-change', handleTextChange);
      editor.on('selection-change', handleSelectionChange);
    };

    const waitForEditorToLoad = setInterval(() => {
      if (!quillEditorRef.current) {
        return;
      }

      clearInterval(waitForEditorToLoad);

      currentEditor = quillEditorRef.current.getEditor();
      currentSocket = io(import.meta.env.VITE_ROOT, {
        path: '/api/socket/socket.io',
        withCredentials: true,
      });

      handleSocketFunctionality(currentSocket, currentEditor);
    }, 50);

    return () => {
      clearInterval(waitForEditorToLoad);

      if (currentSocket) {
        currentSocket.disconnect();

        currentEditor.off('text-change', handleTextChange);
        currentEditor.off('selection-change', handleSelectionChange);
      }
    };
  }, [generatedContentId, email, quillEditorRef]);

  let isFirstChunk = true;
  let currentInsertionPoint;

  const handleTextEdited = (editedTextChunk) => {
    const quill = quillEditorRef.current.getEditor();

    if (isFirstChunk) {
      currentInsertionPoint = selectedTextRange.index;
      quill.deleteText(selectedTextRange.index, selectedTextRange.length, 'user');

      isFirstChunk = false;
    }

    quill.insertText(currentInsertionPoint, editedTextChunk, 'user');

    currentInsertionPoint += editedTextChunk.length;
  };

  return (
    <>
      <div className="mainWrap">
        <div className="clientsList">
          {clients.map((client, index) => (
            <Client key={index} email={client} isCurrentUser={client === email} />
          ))}
        </div>

        <div className="editorWrap">
          <div ref={editorContainerRef} className="editor-container">
            <ReactQuill
              ref={quillEditorRef}
              theme="snow"
              modules={{
                cursors: {
                  hideDelayMs: 500,
                  hideSpeedMs: 300,
                  selectionChangeSource: null,
                  transformOnTextChange: true,
                },
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'blockquote'],
                  [{ list: 'bullet' }, { list: 'ordered' }],
                  ['link', 'image'],
                ],
              }}
              placeholder="Start collaborating As a Team..."
            />
            {textSelectionEditorOpen && (
              <TextSelectionEditor
                textSelectionEditorPosition={textSelectionEditor}
                handleTextSelectionEditorOpen={handleTextSelectionEditorOpen}
                selectedText={selectedText}
                onTextEdited={handleTextEdited}
                aiModel={aiModel}
                provider={provider}
                setError={setError}
                controllerRef={controllerRef}
                setShowAbortButton={setShowAbortButton}
              />
            )}
          </div>
        </div>
      </div>

      <ErrorMessage message={error?.message} />
    </>
  );
};

export default CollaborativeEditor;
